import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  Copy,
  FileText,
  Globe,
  Image as ImageIcon,
  ListTree,
  Mail,
  MoreHorizontal,
  Pencil,
  Phone,
  Sparkles,
  Tag,
  TrendingUp,
  Users,
  LucideIcon,
} from 'lucide-react';
import { DashboardLayout } from '../../components/dashboard';
import { useAuth } from '../../contexts/AuthContext';
import { organizationService } from '../../services/organizationService';
import { ConfidentialityPanel, LockMarker } from '../../features/confidentiality';
import type { Confidentiality } from '../../features/confidentiality';
import { Organization } from '../../types/organization';
import { stakeholderService } from '../../services/stakeholderService';
import { Stakeholder } from '../../types/stakeholder';
import { mediaService } from '../../services/mediaService';
import { AuthImage } from '../../components/media/AuthImage';
import { MediaAsset } from '../../types/media';
import { wikiService } from '../../services/wikiService';
import { WikiPage } from '../../types/wiki';
import { InfluenceLineChart } from '../../components/charts/InfluenceLineChart';
import { getEntityInfluenceData, InfluenceScore, InfluenceCategory } from '../../services/influencesService';
import { ReportGenerator } from '../../components/reports';
import { IntelligenceReportGenerator } from '../../components/intelligence';
import { WikiPageManager } from '../../components/wiki/WikiPageManager';
import { WikiExcerpt } from '../../components/wiki/WikiExcerpt';
import { EntityMonitoringPanel } from '../../features/monitoring/components/EntityMonitoringPanel';
import { SubjectIssuesSection } from '../../features/issues/components/SubjectIssuesSection';
import { ProfileNetwork } from '../../features/intelligence/profile/ProfileNetwork';
import { AreasOfInterest } from '../../features/intelligence/profile/AreasOfInterest';
import {
  FormerNameNote,
  NameHistory,
  useNameHistory,
} from '../../features/government/components/NameHistory';
import { RenameDialog } from '../../features/government/components/RenameDialog';
import { useToast } from '../../components/common';
import { useTaxonomyColors } from '../../hooks/useTaxonomyColors';
import { asOneOf, useUrlState } from '../../hooks/useUrlState';
import { useMissingMedia } from '../../utils/missingMedia';
import {
  Avatar,
  Button,
  Card,
  CategoryPill,
  EmptyState,
  EntityIcon,
  IconButton,
  Menu,
  MenuItem,
  Page,
  Pill,
  SectionHeader,
  Skeleton,
  ScoreBar,
  StatGrid,
  Tabs,
  hexAlpha,
  initialsOf,
  isHexColor,
  readableOn,
  statusTone,
} from '../../components/ui';

/* ---------- helpers ---------- */

const statusBadge = (status: string) => (
  <Pill tone={statusTone(status)}>{status.charAt(0).toUpperCase() + status.slice(1)}</Pill>
);

/** "religious-organization" → "Religious organization" */
const typeLabel = (type?: string) => {
  if (!type) return '';
  // Only reformat slugs; labels such as "Non-Governmental Organization (NGO)" stay as they are
  if (!/^[a-z0-9]+([-_][a-z0-9]+)*$/.test(type)) return type;
  const text = type.replace(/[-_]+/g, ' ');
  // Short single words are acronyms: "ngo" → "NGO"
  if (!text.includes(' ') && text.length <= 4) return text.toUpperCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const getInitials = (s: Stakeholder) =>
  `${(s.firstName || '').charAt(0)}${(s.lastName || '').charAt(0)}`.toUpperCase();

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '';

const prettyUrl = (url: string) => url.replace(/^https?:\/\//, '').replace(/\/$/, '');

const valueLink = 'truncate transition-colors hover:text-primary-700';

const TABS = ['overview', 'people', 'structure', 'influence', 'media', 'wiki', 'monitoring'] as const;
type TabValue = (typeof TABS)[number];

/* One codec per parameter, made once: the URL state reads them on every render */
const TAB = asOneOf(TABS);

/* ---------- skeleton ---------- */

const PageSkeleton: React.FC = () => (
  <Page>
    <Skeleton className="h-7 w-32 rounded-full" />
    <div className="mt-3 flex flex-col items-center gap-3 sm:mt-2 sm:flex-row sm:gap-4">
      <Skeleton className="h-[72px] w-[72px] rounded-2xl sm:h-12 sm:w-12 sm:rounded-xl" />
      <div className="flex flex-col items-center gap-2 sm:items-start">
        <Skeleton className="h-6 w-56" />
        <Skeleton className="h-4 w-36" />
      </div>
    </div>
    <div className="mt-6 flex gap-5 border-b border-neutral-200/70 pb-3">
      {['w-16', 'w-12', 'w-14', 'w-12', 'w-10'].map((width, i) => (
        <Skeleton key={i} className={`h-4 ${width}`} />
      ))}
    </div>
    <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
      <div className="space-y-6">
        <Skeleton className="h-[88px] w-full rounded-2xl" />
        <Skeleton className="h-48 w-full rounded-2xl" />
      </div>
      <Skeleton className="h-64 w-full rounded-2xl" />
    </div>
  </Page>
);

/* ---------- sub-components ---------- */

interface AssociatedStakeholder {
  stakeholder: Stakeholder;
  role: string;
  department: string;
  isCurrent: boolean;
  startDate: string;
  endDate?: string;
}

const BackLink: React.FC = () => (
  <Link
    to="/organizations"
    className="-ml-1 inline-flex h-7 items-center gap-0.5 rounded-full pl-0.5 pr-2 text-[13px] text-neutral-500 transition-colors hover:text-ink"
  >
    <ChevronLeft className="h-4 w-4" strokeWidth={1.75} />
    Organizations
  </Link>
);

/** Teal rounded-square initials, or the logo when there is one */
const OrgLogo: React.FC<{
  org: Pick<Organization, 'name' | 'logoId' | 'logoUrl'>;
  size: 'xs' | 'xl';
  className?: string;
}> = ({ org, size, className = '' }) => {
  const image = org.logoId ? (
    <AuthImage
      mediaId={org.logoId}
      alt={org.name}
      className="h-full w-full object-cover"
      fallback={initialsOf(org.name)}
    />
  ) : org.logoUrl ? (
    <img src={org.logoUrl} alt={org.name} className="h-full w-full object-cover" />
  ) : undefined;
  return <Avatar kind="organization" size={size} name={org.name} image={image} className={className} />;
};

/** Stat mark for influence, tinted with the colour of the strongest area */
const AreaIcon: React.FC<{ color?: string }> = ({ color }) => (
  <span
    aria-hidden="true"
    className={`inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md ${
      isHexColor(color) ? '' : 'bg-neutral-100 text-neutral-500'
    }`}
    style={isHexColor(color) ? { backgroundColor: hexAlpha(color, 0.1), color: readableOn(color) } : undefined}
  >
    <TrendingUp className="h-3 w-3" strokeWidth={1.75} />
  </span>
);

const FactRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex min-h-[40px] items-center justify-between gap-4 px-4 py-2 text-[13px]">
    <span className="flex-shrink-0 text-neutral-500">{label}</span>
    <span className="flex min-w-0 items-center justify-end text-right text-ink">{children}</span>
  </div>
);

const PersonRow: React.FC<{ person: AssociatedStakeholder; onOpen: () => void }> = ({ person, onOpen }) => {
  const { stakeholder: st, role, department, isCurrent } = person;
  const detail = [role !== '—' ? role : '', department !== '—' ? department : ''].filter(Boolean).join(' · ');
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="flex min-h-[52px] w-full items-center gap-3 px-4 py-2 text-left transition-colors hover:bg-neutral-50 focus-visible:bg-neutral-50 focus-visible:outline-none active:bg-neutral-100"
      >
        <Avatar
          kind="stakeholder"
          size="sm"
          name={`${st.firstName || ''} ${st.lastName || ''}`}
          initials={getInitials(st) || undefined}
          image={
            st.profileImageId ? (
              <AuthImage
                mediaId={st.profileImageId}
                alt=""
                className="h-full w-full object-cover"
                fallback={getInitials(st)}
              />
            ) : undefined
          }
        />
        <span className="min-w-0 flex-1">
          <span className={`block truncate text-[13px] ${isCurrent ? 'text-ink' : 'text-neutral-500'}`}>
            {st.firstName} {st.lastName}
          </span>
          <span className="mt-0.5 block truncate text-[12px] text-neutral-400">{detail || st.professionalEmail || '—'}</span>
        </span>
        {st.professionalEmail && detail && (
          <span className="hidden max-w-[220px] truncate text-[12px] text-neutral-400 md:block">{st.professionalEmail}</span>
        )}
        {isCurrent ? <Pill tone="green">Current</Pill> : <Pill tone="outline">Former</Pill>}
      </button>
    </li>
  );
};

const OrgLinkRow: React.FC<{ org: Pick<Organization, 'id' | 'name' | 'type' | 'logoId' | 'logoUrl'>; label?: string }> = ({
  org,
  label,
}) => (
  <Link
    to={`/organizations/${org.id}`}
    className="group flex min-h-[48px] items-center gap-3 px-4 py-2 transition-colors hover:bg-neutral-50 active:bg-neutral-100"
  >
    <OrgLogo org={org} size="xs" />
    <span className="min-w-0 flex-1">
      <span className="block truncate text-[13px] text-ink">{org.name}</span>
      {(label || org.type) && (
        <span className="block truncate text-[12px] text-neutral-400">{label || typeLabel(org.type)}</span>
      )}
    </span>
    <ChevronRight className="h-4 w-4 flex-shrink-0 text-neutral-300 transition-colors group-hover:text-ink" strokeWidth={1.75} />
  </Link>
);

/* ---------- main component ---------- */

/** A body that no longer exists, and what took over — or what this one replaced */
const SuccessionNote: React.FC<{ org: Organization }> = ({ org }) => {
  const links = (items: Array<{ id: string; name: string }>) =>
    items.map((item, index) => (
      <React.Fragment key={item.id}>
        {index > 0 && (index === items.length - 1 ? ' and ' : ', ')}
        <Link to={`/organizations/${item.id}`} className="text-ink underline decoration-neutral-300 underline-offset-2">
          {item.name}
        </Link>
      </React.Fragment>
    ));
  const after = org.replacedBy ?? [];
  const before = org.replaced ?? [];
  if (!org.endedOn && !before.length) return null;
  const how =
    org.endedHow === 'split' ? 'Split into' : org.endedHow === 'merge' ? 'Merged into' : 'Replaced by';
  return (
    <div className="mt-1.5 space-y-1 text-[12.5px] leading-snug text-neutral-500">
      {org.endedOn && (
        <p className="flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 sm:justify-start">
          <span className="rounded-full bg-neutral-200/70 px-1.5 py-px text-[10px] uppercase tracking-[0.05em] text-neutral-500">
            Defunct
          </span>
          <span>
            {org.endedHow === 'dissolved' || !after.length ? 'No longer exists' : <>{how} {links(after)}</>}
            {' · '}
            {new Date(org.endedOn).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
          </span>
        </p>
      )}
      {before.length > 0 && <p>Replaced {links(before)}, which no longer {before.length > 1 ? 'exist' : 'exists'}.</p>}
    </div>
  );
};

export const OrganizationDetailPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [org, setOrg] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stakeholders, setStakeholders] = useState<Stakeholder[]>([]);
  const [allMedia, setMedia] = useState<MediaAsset[]>([]);
  const missingMedia = useMissingMedia();
  // A file that has gone from storage is not offered at all: a tile that
  // cannot draw says nothing, and the count would be wrong besides
  const media = useMemo(() => allMedia.filter((a) => !missingMedia(a.id)), [allMedia, missingMedia]);
  const [wikiPages, setWikiPages] = useState<WikiPage[]>([]);
  const [influenceScores, setInfluenceScores] = useState<InfluenceScore[]>([]);
  const [influenceCategories, setInfluenceCategories] = useState<InfluenceCategory[]>([]);
  const [loadingInfluence, setLoadingInfluence] = useState(false);
  // Which section is open is in the address bar, so a link opens the record
  // where the sender was looking. A link to another organization carries no
  // section of its own, so moving on still lands on the overview.
  const [openTab, setActiveTab] = useUrlState<TabValue>('tab', 'overview', TAB);
  /** Kept beside the record so the marker updates without a refetch */
  const [confidentiality, setConfidentiality] = useState<Confidentiality | null>(null);
  const [generatorKey, setGeneratorKey] = useState(0);
  const [renaming, setRenaming] = useState(false);
  const { showSuccess } = useToast();
  const { colorOf } = useTaxonomyColors();
  const { user } = useAuth();
  /** Read once here so the header note and the card do not both fetch it */
  const { changes: nameChanges, reload: reloadNames } = useNameHistory(id);

  // The report generators render their own trigger; we hide it and open them from our menu
  const reportHostRef = useRef<HTMLDivElement>(null);
  const intelligenceHostRef = useRef<HTMLDivElement>(null);
  const openFrom = (host: { current: HTMLDivElement | null }) => host.current?.querySelector('button')?.click();

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      try {
        setLoading(true);
        setError(null);
        const [o, s, m, w] = await Promise.all([
          organizationService.getOrganization(id),
          stakeholderService.getStakeholders({ organizationId: id, page: 1, limit: 100 }),
          mediaService.getMediaAssets({ organizationId: id }),
          wikiService.getWikiPagesByEntity('organization', id),
        ]);
        setOrg(o);
        setStakeholders((s?.data ?? []) as Stakeholder[]);
        setMedia((m || []).filter((a) => a.mimeType?.startsWith('image/')));
        setWikiPages(w || []);

        try {
          setLoadingInfluence(true);
          const influenceData = await getEntityInfluenceData('organization', id);
          setInfluenceScores(influenceData.scores);
          setInfluenceCategories(influenceData.categories);
        } catch {
          setInfluenceScores([]);
          setInfluenceCategories([]);
        } finally {
          setLoadingInfluence(false);
        }
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load organization');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  /* ---- derived data ---- */

  // Associated stakeholders ranked: current first, then alphabetically
  const associatedStakeholders = useMemo<AssociatedStakeholder[]>(() => {
    if (!id) return [];
    const results: AssociatedStakeholder[] = [];
    stakeholders.forEach((st) => {
      const match = (st.assignments || []).find((a) => a.organizationId === id);
      if (match) {
        results.push({
          stakeholder: st,
          role: match.role || '—',
          department: match.department || '—',
          isCurrent: match.isCurrent,
          startDate: match.startDate,
          endDate: match.endDate,
        });
      }
    });
    results.sort((a, b) => {
      if (a.isCurrent !== b.isCurrent) return a.isCurrent ? -1 : 1;
      return `${a.stakeholder.firstName} ${a.stakeholder.lastName}`.localeCompare(
        `${b.stakeholder.firstName} ${b.stakeholder.lastName}`
      );
    });
    return results;
  }, [stakeholders, id]);

  // Weighted influence score: current stakeholders weighted 2x, former 0.5x
  const computedInfluenceScore = useMemo(() => {
    const orgScore = (org as any)?.influenceScore;
    if (!orgScore || orgScore <= 0) return null;
    if (associatedStakeholders.length === 0) return orgScore;

    const currentCount = associatedStakeholders.filter(s => s.isCurrent).length;
    const totalCount = associatedStakeholders.length;
    const activeRatio = currentCount / totalCount;

    // Adjust score: boost up to 20% when active ratio is high
    return Math.min(100, Math.round(orgScore * (1 + activeRatio * 0.2)));
  }, [org, associatedStakeholders]);

  /** The areas its influence has been assessed in, strongest first */
  const influenceAreas = useMemo(
    () =>
      influenceScores.flatMap((score) => {
        const category = score.category || influenceCategories.find((c) => c.id === score.categoryId);
        return category
          ? [{ label: category.label, color: category.color ?? null, score: score.score }]
          : [];
      }),
    [influenceScores, influenceCategories],
  );

  // Strongest influence area, for the overview stat
  const topArea = useMemo(() => {
    const top = influenceScores.reduce<InfluenceScore | null>(
      (best, score) => (!best || score.score > best.score ? score : best),
      null
    );
    if (!top) return null;
    const category = top.category || influenceCategories.find((c) => c.id === top.categoryId);
    return category ? { label: category.label, color: category.color } : null;
  }, [influenceScores, influenceCategories]);

  const wikiSnippet = useMemo(() => (wikiPages[0]?.content ?? '').trim(), [wikiPages]);

  const hasParent = !!org?.parent;
  const hasChildren = (org?.children?.length ?? 0) > 0;
  const hasHierarchy = hasParent || hasChildren;

  /**
   * A section this record does not have — structure, for an organization that
   * stands alone — reads as the overview rather than as a page with nothing
   * on it.
   */
  const activeTab: TabValue = openTab === 'structure' && !hasHierarchy ? 'overview' : openTab;

  const contactItems = useMemo(() => {
    if (!org) return [];
    const items: { icon: LucideIcon; label: string; value: string; href?: string }[] = [];
    if (org.email) items.push({ icon: Mail, label: 'Email', value: org.email, href: `mailto:${org.email}` });
    if (org.phone) items.push({ icon: Phone, label: 'Call', value: org.phone, href: `tel:${org.phone}` });
    if (org.website) items.push({ icon: Globe, label: 'Website', value: org.website, href: org.website.startsWith('http') ? org.website : `https://${org.website}` });
    return items;
  }, [org]);

  /* ---------- render ---------- */

  if (loading) {
    return (
      <DashboardLayout>
        <PageSkeleton />
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <Page>
          <BackLink />
          <p role="alert" className="mt-4 flex flex-wrap items-center gap-x-2 rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">
            {error}
            <button type="button" onClick={() => navigate('/organizations')} className="font-medium underline underline-offset-2">
              Back to list
            </button>
          </p>
        </Page>
      </DashboardLayout>
    );
  }

  if (!org) return null;

  const currentCount = associatedStakeholders.filter(s => s.isCurrent).length;
  const formerCount = associatedStakeholders.length - currentCount;
  const childCount = org.children?.length ?? 0;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      showSuccess('Link copied');
    } catch {
      /* clipboard unavailable */
    }
  };

  const canRename = !!user && ['admin', 'super_admin', 'top_owner'].includes(user.role);

  const menuItems: MenuItem[] = [
    ...(canRename ? [{ label: 'Rename', icon: Tag, onSelect: () => setRenaming(true) }] : []),
    { label: 'Report', icon: FileText, onSelect: () => openFrom(reportHostRef) },
    { label: 'Intelligence', icon: Sparkles, onSelect: () => openFrom(intelligenceHostRef) },
    { label: 'Show in tree', icon: ListTree, onSelect: () => navigate('/organizations?view=hierarchy') },
    { label: 'Copy link', icon: Copy, onSelect: copyLink },
  ];

  const actions = (
    <>
      <Button size="sm" variant="secondary" onClick={() => navigate(`/organizations/${id}/edit`)}>
        <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} />
        Edit
      </Button>
      <Menu
        items={menuItems}
        trigger={(props) => (
          <IconButton label="More actions" size="sm" className="ring-1 ring-inset ring-neutral-200" {...props}>
            <MoreHorizontal className="h-4 w-4" strokeWidth={1.75} />
          </IconButton>
        )}
      />
    </>
  );

  /* ---- blocks ---- */

  const details = (
    <section>
      <SectionHeader title="Details" />
      <Card divided>
        {org.type && (
          <FactRow label="Type">
            <CategoryPill title={org.type} label={typeLabel(org.type)} color={colorOf('organization_type', org.type)} />
          </FactRow>
        )}
        {/* An approved record is the normal case; saying so is noise */}
        {org.status !== 'approved' && <FactRow label="Status">{statusBadge(org.status)}</FactRow>}
        {(confidentiality ?? org.confidentiality ?? 'normal') !== 'normal' && (
          <FactRow label="Held">
            <LockMarker
              level={confidentiality ?? org.confidentiality ?? 'normal'}
              variant="pill"
            />
          </FactRow>
        )}
        {hasParent && (
          <FactRow label="Parent">
            <Link to={`/organizations/${org.parent!.id}`} className={valueLink}>
              {org.parent!.name}
            </Link>
          </FactRow>
        )}
        {(org.acronym || (org.aliases?.length ?? 0) > 0) && (
          <FactRow label="Also known as">
            <span className="line-clamp-2 text-right [overflow-wrap:anywhere]">
              {[org.acronym, ...(org.aliases ?? [])].filter(Boolean).join(' · ')}
            </span>
          </FactRow>
        )}
        {org.email && (
          <FactRow label="Email">
            <a href={`mailto:${org.email}`} className={valueLink}>{org.email}</a>
          </FactRow>
        )}
        {org.phone && (
          <FactRow label="Phone">
            <a href={`tel:${org.phone}`} className={`${valueLink} tabular-nums`}>{org.phone}</a>
          </FactRow>
        )}
        {org.website && (
          <FactRow label="Website">
            <a
              href={org.website.startsWith('http') ? org.website : `https://${org.website}`}
              target="_blank"
              rel="noopener noreferrer"
              className={valueLink}
            >
              {prettyUrl(org.website)}
            </a>
          </FactRow>
        )}
        {org.address && (
          <FactRow label="Address">
            <span className="line-clamp-2 [overflow-wrap:anywhere] whitespace-pre-line">{org.address}</span>
          </FactRow>
        )}
      </Card>
      <p className="mt-2 px-1 text-[12px] text-neutral-400">
        Added {formatDate(org.createdAt)}
        {org.updatedAt && ` · Updated ${formatDate(org.updatedAt)}`}
      </p>
    </section>
  );

  const peopleList = (people: AssociatedStakeholder[]) => (
    <Card>
      <ul className="divide-y divide-neutral-200/70">
        {people.map((person) => (
          <PersonRow
            key={person.stakeholder.id}
            person={person}
            onOpen={() => navigate(`/stakeholders/${person.stakeholder.id}`)}
          />
        ))}
      </ul>
    </Card>
  );

  const PREVIEW_PEOPLE = 5;

  return (
    <DashboardLayout>
      <Page>
        {/* ==================== HEADER ==================== */}
        <header className="mb-5">
          <div className="flex min-h-[32px] items-center justify-between gap-2">
            <BackLink />
            <div className="flex items-center gap-2 sm:hidden">{actions}</div>
          </div>

          <div className="mt-3 flex flex-col items-center gap-3 text-center sm:mt-2 sm:flex-row sm:gap-4 sm:text-left">
            <OrgLogo org={org} size="xl" className="h-[72px] w-[72px] sm:h-12 sm:w-12 sm:rounded-xl sm:text-[17px]" />
            <div className="min-w-0 max-w-full flex-1">
              <h1 className="line-clamp-2 break-words text-[24px] font-normal leading-tight tracking-[-0.03em] text-ink sm:line-clamp-none sm:truncate">
                {org.name}
              </h1>
              <FormerNameNote organizationId={org.id} changes={nameChanges} className="mt-1" />
              <SuccessionNote org={org} />
              <p className="mt-1 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[13px] text-neutral-500 sm:justify-start">
                <span className="min-w-0 truncate">
                  {typeLabel(org.type) || 'Organization'}
                  {hasParent && (
                    <>
                      {' · '}
                      <Link to={`/organizations/${org.parent!.id}`} className="transition-colors hover:text-ink">
                        {org.parent!.name}
                      </Link>
                    </>
                  )}
                </span>
                {org.status !== 'approved' && statusBadge(org.status)}
              </p>
            </div>
            <div className="hidden flex-shrink-0 items-center gap-2 sm:flex">{actions}</div>
          </div>

          {contactItems.length > 0 && (
            <div
              className="mt-5 grid gap-2 sm:hidden"
              style={{ gridTemplateColumns: `repeat(${contactItems.length}, minmax(0, 1fr))` }}
            >
              {contactItems.map(({ label, icon: Icon, href }) => (
                <a
                  key={label}
                  href={href}
                  {...(href?.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  className="flex h-14 flex-col items-center justify-center gap-1 rounded-2xl bg-neutral-100 text-[11px] text-ink transition-colors active:bg-neutral-200"
                >
                  <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
                  {label}
                </a>
              ))}
            </div>
          )}
        </header>

        {/* ==================== TABS ==================== */}
        <Tabs<TabValue>
          label="Organization sections"
          className="mb-5"
          value={activeTab}
          onChange={setActiveTab}
          items={[
            { value: 'overview', label: 'Overview' },
            { value: 'people', label: 'People', count: associatedStakeholders.length },
            ...(hasHierarchy ? [{ value: 'structure' as const, label: 'Structure', count: childCount }] : []),
            { value: 'influence', label: 'Influence' },
            { value: 'media', label: 'Media', count: media.length },
            { value: 'wiki', label: 'Wiki', count: wikiPages.length },
            { value: 'monitoring', label: 'Monitoring' },
          ]}
        />

        {/* ==================== OVERVIEW TAB ==================== */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
            {/* ---- main column ---- */}
            <div className="min-w-0 space-y-6">
              <StatGrid
                items={[
                  {
                    label: 'People',
                    icon: <EntityIcon kind="stakeholder" size="xs" />,
                    value: currentCount,
                    hint: formerCount ? `${formerCount} former` : 'Current',
                  },
                  {
                    label: 'Sub-orgs',
                    icon: <EntityIcon kind="organization" size="xs" />,
                    value: childCount,
                    hint: hasParent ? `Under ${org.parent!.name}` : 'Top level',
                  },
                  {
                    label: 'Influence',
                    icon: <AreaIcon color={computedInfluenceScore !== null ? topArea?.color : undefined} />,
                    value: computedInfluenceScore !== null ? computedInfluenceScore : '—',
                    hint:
                      computedInfluenceScore === null ? (
                        'No score'
                      ) : topArea ? (
                        <span title="Strongest area">{topArea.label}</span>
                      ) : (
                        'of 100'
                      ),
                  },
                  {
                    label: 'Media',
                    icon: <EntityIcon kind="media" size="xs" />,
                    value: media.length,
                    hint: `${wikiPages.length} wiki page${wikiPages.length === 1 ? '' : 's'}`,
                  },
                ]}
              />

              {org.description && (
                <section>
                  <SectionHeader title="About" />
                  <p className="whitespace-pre-line text-[14px] leading-relaxed text-neutral-700">{org.description}</p>
                </section>
              )}

              <div className="lg:hidden">{details}</div>

              <section>
                <SectionHeader
                  title="People"
                  aside={
                    associatedStakeholders.length > PREVIEW_PEOPLE ? (
                      <Button size="xs" variant="ghost" onClick={() => setActiveTab('people')}>
                        All {associatedStakeholders.length}
                      </Button>
                    ) : associatedStakeholders.length > 0 ? (
                      <span className="tabular-nums">
                        {currentCount} current{formerCount > 0 && ` · ${formerCount} former`}
                      </span>
                    ) : undefined
                  }
                />
                {associatedStakeholders.length === 0 ? (
                  <Card>
                    <EmptyState icon={Users} title="No people yet" className="py-8" />
                  </Card>
                ) : (
                  peopleList(associatedStakeholders.slice(0, PREVIEW_PEOPLE))
                )}
              </section>

              <section>
                <SectionHeader
                  title="Wiki"
                  aside={
                    <Button size="xs" variant="ghost" onClick={() => setActiveTab('wiki')}>
                      {wikiPages.length > 0 ? 'Open' : 'Generate'}
                    </Button>
                  }
                />
                <WikiExcerpt html={wikiSnippet} onOpen={() => setActiveTab('wiki')} />
              </section>

              {/* Who it is tied to, and who can reach it */}
              <ProfileNetwork id={id} />

              {/* Issues this organization has taken a position on */}
              <SubjectIssuesSection subjectType="organization" subjectId={id} />
            </div>

            {/* ---- side column ---- */}
            <div className="space-y-6">
              <div className="hidden lg:block">{details}</div>

              <AreasOfInterest subjectType="organization" subjectId={id} influence={influenceAreas} />

              <section>
                <SectionHeader
                  title="Media"
                  aside={
                    media.length > 0 ? (
                      <Button size="xs" variant="ghost" onClick={() => setActiveTab('media')}>
                        All {media.length}
                      </Button>
                    ) : undefined
                  }
                />
                {media.length > 0 ? (
                  <div className="grid grid-cols-3 gap-1.5">
                    {media.slice(0, 6).map((asset, index) => (
                      <button
                        key={asset.id}
                        type="button"
                        onClick={() => setActiveTab('media')}
                        className="relative aspect-square overflow-hidden rounded-xl bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
                      >
                        <AuthImage mediaId={asset.id} alt={asset.originalName} className="h-full w-full object-cover" />
                        {index === 5 && media.length > 6 && (
                          <span className="absolute inset-0 flex items-center justify-center bg-ink/50 text-[13px] tabular-nums text-white">
                            +{media.length - 6}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                ) : (
                  <Card>
                    <EmptyState icon={ImageIcon} title="No media" className="py-8" />
                  </Card>
                )}
              </section>

              {/* The names this entity has carried */}
              {nameChanges.length > 0 && (
                <section>
                  <SectionHeader title="Name history" />
                  <NameHistory organizationId={org.id} currentName={org.name} changes={nameChanges} />
                </section>
              )}

              {/* Who can see this record */}
              <section>
                <ConfidentialityPanel
                  entityType="organization"
                  entityId={org.id}
                  onChanged={(summary) => setConfidentiality(summary.confidentiality)}
                />
              </section>
            </div>
          </div>
        )}

        {/* ==================== PEOPLE TAB ==================== */}
        {activeTab === 'people' && (
          <section>
            <SectionHeader
              title="People"
              aside={
                associatedStakeholders.length > 0 && (
                  <span className="tabular-nums">
                    {currentCount} current{formerCount > 0 && ` · ${formerCount} former`}
                  </span>
                )
              }
            />
            {associatedStakeholders.length === 0 ? (
              <Card>
                <EmptyState icon={Users} title="No people yet" />
              </Card>
            ) : (
              peopleList(associatedStakeholders)
            )}
          </section>
        )}

        {/* ==================== STRUCTURE TAB ==================== */}
        {activeTab === 'structure' && hasHierarchy && (
          <div className="max-w-2xl space-y-6">
            {hasParent && (
              <section>
                <SectionHeader title="Parent" />
                <Card>
                  <OrgLinkRow org={org.parent!} />
                </Card>
              </section>
            )}
            <section>
              <SectionHeader
                title="Sub-organizations"
                aside={childCount > 0 && <span className="tabular-nums">{childCount}</span>}
              />
              <Card>
                <div className="flex min-h-[48px] items-center gap-3 bg-neutral-50/70 px-4 py-2">
                  <OrgLogo org={org} size="xs" />
                  <span className="min-w-0 flex-1 truncate text-[13px] text-ink">{org.name}</span>
                  <Pill tone="outline">This</Pill>
                </div>
                {hasChildren ? (
                  <ul className="border-t border-neutral-200/70">
                    {org.children!.map((child) => (
                      <li key={child.id} className="relative pl-6">
                        <span aria-hidden="true" className="absolute inset-y-0 left-[27px] w-px bg-neutral-200" />
                        <OrgLinkRow org={child} />
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="border-t border-neutral-200/70 px-4 py-3 text-[13px] text-neutral-400">None</p>
                )}
              </Card>
            </section>
          </div>
        )}

        {/* ==================== INFLUENCE TAB ==================== */}
        {activeTab === 'influence' && (
          <section>
            <SectionHeader title="Influence" />
            <Card>
              {computedInfluenceScore !== null && (
                <div className="border-b border-neutral-200/70 p-4">
                  <div className="flex items-end justify-between gap-3">
                    <div>
                      <p className="text-[12px] text-neutral-500">Weighted score</p>
                      <p className="mt-2 text-[22px] leading-none tracking-[-0.02em] tabular-nums text-ink">
                        {computedInfluenceScore}
                        <span className="text-[13px] text-neutral-400"> / 100</span>
                      </p>
                    </div>
                    <p className="text-right text-[12px] text-neutral-400" title="Current people weigh more than former">
                      {currentCount} current · {formerCount} former
                    </p>
                  </div>
                  <ScoreBar value={computedInfluenceScore} className="mt-3" />
                </div>
              )}

              {loadingInfluence ? (
                <div className="p-4">
                  <Skeleton className="h-56 w-full rounded-xl" />
                </div>
              ) : influenceScores.length > 0 ? (
                <div className="overflow-x-auto p-4">
                  <InfluenceLineChart data={influenceScores} categories={influenceCategories} width={600} height={280} />
                </div>
              ) : (
                <EmptyState
                  icon={TrendingUp}
                  title="No category scores"
                  action={
                    <Button size="sm" variant="secondary" onClick={() => navigate(`/organizations/${id}/edit`)}>
                      Add scores
                    </Button>
                  }
                />
              )}
            </Card>
          </section>
        )}

        {/* ==================== MEDIA TAB ==================== */}
        {activeTab === 'media' && (
          <section>
            <SectionHeader title="Media" aside={media.length > 0 && <span className="tabular-nums">{media.length}</span>} />
            {media.length > 0 ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                {media.map((asset) => (
                  <figure key={asset.id} className="min-w-0">
                    <div className="aspect-[4/3] overflow-hidden rounded-2xl bg-neutral-100">
                      {mediaService.isImageFile(asset.mimeType) ? (
                        <AuthImage mediaId={asset.id} alt={asset.originalName} className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-neutral-400">
                          <FileText className="h-6 w-6" strokeWidth={1.75} />
                        </div>
                      )}
                    </div>
                    <figcaption className="mt-1.5 px-0.5">
                      <p className="truncate text-[13px] text-ink" title={asset.originalName}>{asset.originalName}</p>
                      <p className="text-[12px] tabular-nums text-neutral-400">{formatDate(asset.createdAt)}</p>
                    </figcaption>
                  </figure>
                ))}
              </div>
            ) : (
              <Card>
                <EmptyState icon={ImageIcon} title="No media" />
              </Card>
            )}
          </section>
        )}

        {/* ==================== WIKI TAB ==================== */}
        {activeTab === 'wiki' && (
          <section>
            <SectionHeader
              title="Wiki"
              aside={
                <Button size="sm" variant="dark" onClick={() => openFrom(intelligenceHostRef)}>
                  <Sparkles className="h-4 w-4" strokeWidth={1.75} />
                  Generate
                </Button>
              }
            />
            <WikiPageManager
              key={generatorKey}
              entityType="organization"
              entityId={id!}
              entityName={org.name}
            />
          </section>
        )}

        {/* ==================== MONITORING TAB ==================== */}
        {activeTab === 'monitoring' && (
          <EntityMonitoringPanel subjectType="organization" subjectId={id!} subjectLabel={org.name} />
        )}

        {canRename && (
          <RenameDialog
            open={renaming}
            organizationId={org.id}
            currentName={org.name}
            currentAcronym={org.acronym ?? null}
            onClose={() => setRenaming(false)}
            onDone={(newName) => {
              setOrg((current) => (current ? { ...current, name: newName } : current));
              void reloadNames();
            }}
          />
        )}

        {/* Report and intelligence dialogs; their own trigger buttons stay hidden */}
        <div ref={reportHostRef} className="contents [&>button:first-child]:hidden">
          <ReportGenerator
            entity={org}
            entityType="organization"
            media={media}
            wikiPages={wikiPages}
            influenceScores={influenceScores}
            influenceCategories={influenceCategories}
            stakeholders={stakeholders}
          />
        </div>
        <div ref={intelligenceHostRef} className="contents [&>button:first-child]:hidden">
          <IntelligenceReportGenerator
            entityType="organization"
            entityId={id!}
            entityName={org.name}
            onComplete={() => {
              setGeneratorKey(k => k + 1);
              wikiService.getWikiPagesByEntity('organization', id!).then(w => setWikiPages(w || []));
            }}
          />
        </div>
      </Page>
    </DashboardLayout>
  );
};
